/**
 * Layout constants and spacer component reserving scroll-flow space for the collapsed
 * NotificationsPanel.
 *
 * The panel itself is rendered via a `height: 0` sticky wrapper (see NotificationsPanel.tsx),
 * so it contributes nothing to the document flow and can visually overlap page content below
 * it — most notably the bottom-most button of the quote card on pages with tall content
 * (e.g. multiple quantities in the "Mengen und Preise" card).
 *
 * The exact space to reserve depends on the collapsed panel's rendered height, which varies
 * with font, theme and translations. To avoid the earlier bug of a hard-coded height that was
 * too small (leaving the button only a few pixels clear), `NotificationsWithPanel` measures the
 * live header height at runtime and passes it in via the `height` prop. The constants below are
 * only a fallback used before the first measurement.
 *
 * `PANEL_BOTTOM_OFFSET_PX` and `COLLAPSED_PANEL_RESERVED_HEIGHT_PX` are two distinct sizes
 * with a single shared origin for the offset summand:
 * - `PANEL_BOTTOM_OFFSET_PX` is the sticky wrapper's `bottom` offset from the viewport floor
 *   (reused as-is in NotificationsPanel.tsx — do NOT replace with the reserved height).
 * - `COLLAPSED_PANEL_RESERVED_HEIGHT_PX` is the fallback total scroll-flow space to reserve so
 *   the collapsed panel never overlaps the content above it: collapsed header height + bottom
 *   offset + a comfortable buffer.
 *
 * These must stay separate: reusing the reserved height as the sticky `bottom` offset would
 * silently move the panel away from the viewport floor instead of the intended 16px.
 */

import React from "react";
import {Box} from "@mui/material";

/**
 * Fallback height of the collapsed panel header (icon row + padding), in pixels. Used only until
 * `NotificationsWithPanel` has measured the real header height. Deliberately generous.
 */
export const PANEL_HEADER_HEIGHT_PX = 56;

/** Sticky wrapper's `bottom` offset from the scroll viewport floor, in pixels. */
export const PANEL_BOTTOM_OFFSET_PX = 16;

/** Comfortable safety buffer added on top of header height + bottom offset, in pixels. */
export const PANEL_SPACER_BUFFER_PX = 16;

/**
 * Fallback total scroll-flow height to reserve at the end of the page content so the collapsed
 * NotificationsPanel never overlaps the content above it. Superseded at runtime by the measured
 * value passed through the `height` prop.
 */
export const COLLAPSED_PANEL_RESERVED_HEIGHT_PX =
    PANEL_HEADER_HEIGHT_PX + PANEL_BOTTOM_OFFSET_PX + PANEL_SPACER_BUFFER_PX;

interface PanelReservedSpaceProps {
    /**
     * Exact scroll-flow height to reserve, in pixels (measured collapsed panel height + bottom
     * offset + buffer). Falls back to `COLLAPSED_PANEL_RESERVED_HEIGHT_PX` before measurement.
     */
    height?: number;
}

/**
 * Invisible spacer reserving scroll-flow height at the end of a page's scrollable content, so
 * the collapsed NotificationsPanel never overlaps the content above it.
 *
 * Do not render this directly alongside `NotificationsPanel` in page code — use the
 * `NotificationsWithPanel` wrapper instead, which renders both under one shared visibility
 * condition (single source of truth) and feeds in the measured `height` so the spacer can never
 * reserve space for a panel that is not actually visible, nor too little space to clear it.
 */
const PanelReservedSpace: React.FC<PanelReservedSpaceProps> = ({height = COLLAPSED_PANEL_RESERVED_HEIGHT_PX}) => (
        <Box aria-hidden height={height}/>
);

export default PanelReservedSpace;
